import { useState, useEffect } from 'react';
import './PWAStatus.css';

interface PWAStatusProps {
  className?: string;
}

export function PWAStatus({ className = '' }: PWAStatusProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallButton(false);
      setDeferredPrompt(null);
    };

    // Listen for online/offline status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
    
    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  return (
    <div className={`pwa-status-container ${className}`}>
      <div className="pwa-status-grid">
        <div className="pwa-status-item">
          <span className="pwa-status-label">Installation Status:</span>
          <span className={`pwa-status-value ${isInstalled ? 'pwa-status-value--success' : 'pwa-status-value--info'}`}>
            {isInstalled ? '📱 Installed' : '🌐 Web App'}
          </span>
        </div>
        
        <div className="pwa-status-item">
          <span className="pwa-status-label">Connection:</span>
          <span className={`pwa-status-value ${isOnline ? 'pwa-status-value--success' : 'pwa-status-value--warning'}`}>
            {isOnline ? '🟢 Online' : '🔴 Offline'}
          </span>
        </div>

        <div className="pwa-status-item">
          <span className="pwa-status-label">Service Worker:</span>
          <span className="pwa-status-value pwa-status-value--success">
            ✅ Active
          </span>
        </div>

        {showInstallButton && (
          <div className="pwa-status-item pwa-status-item--full">
            <button 
              onClick={handleInstallClick}
              className="pwa-install-button"
            >
              📱 Install as App
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PWAStatus; 